import type {
  AspectRatioOption,
  StudioModeConfig,
} from '../domain/studioTypes'

export const ASPECT_RATIO_OPTIONS: AspectRatioOption[] = [
  {
    id: '1:1',
    label: 'Kare',
    ratioLabel: '1:1',
    description: 'Instagram Akış, Profil, Sanat',
    widthRatio: 1,
    heightRatio: 1,
  },
  {
    id: '3:4',
    label: 'Portre',
    ratioLabel: '3:4',
    description: 'Portre, Afiş, Editoryal',
    widthRatio: 3,
    heightRatio: 4,
  },
  {
    id: '4:3',
    label: 'Yatay',
    ratioLabel: '4:3',
    description: 'Sunum, Albüm, Editoryal',
    widthRatio: 4,
    heightRatio: 3,
  },
  {
    id: '9:16',
    label: 'Dikey',
    ratioLabel: '9:16',
    description: 'Instagram Story, Reels, TikTok',
    widthRatio: 9,
    heightRatio: 16,
  },
  {
    id: '16:9',
    label: 'Yatay',
    ratioLabel: '16:9',
    description: 'YouTube, Banner, Sinema',
    widthRatio: 16,
    heightRatio: 9,
  },
  {
    id: '4:5',
    label: 'Portre',
    ratioLabel: '4:5',
    description: 'Instagram Portre, Poster, Vitrin',
    widthRatio: 4,
    heightRatio: 5,
  },
  {
    id: '5:4',
    label: 'Yatay',
    ratioLabel: '5:4',
    description: 'Albüm, Sunum, Yatay Portre',
    widthRatio: 5,
    heightRatio: 4,
  },
  {
    id: '21:9',
    label: 'Panoramik',
    ratioLabel: '21:9',
    description: 'Ultra Geniş, Sinematik Manzara',
    widthRatio: 21,
    heightRatio: 9,
  },
  {
    id: '9:21',
    label: 'Uzun Dikey',
    ratioLabel: '9:21',
    description: 'Uzun poster, mobil hikâye',
    widthRatio: 9,
    heightRatio: 21,
  },
]

export const REVISION_QUICK_TWEAKS = [
  { id: 'lighting', label: 'Işığı Değiştir', prompt: 'Işıklandırmayı daha sıcak, sinematik altın saat (golden hour) ışığına dönüştür.' },
  { id: 'background', label: 'Arka Planı Değiştir', prompt: 'Arka planı daha sade, derin ve odak noktasını öne çıkaracak biçimde yenile.' },
  { id: 'clothes', label: 'Kıyafeti Güncelle', prompt: 'Karakterin kıyafetini şık, vintage siyah deri ceketle değiştir.' },
  { id: 'add_object', label: 'Detay Ekle', prompt: 'Kadraja hafif analog film greni ve atmosferik lens yansıması ekle.' },
  { id: 'remove_distraction', label: 'Temizle & Sadeleştir', prompt: 'Arka plandaki dikkat dağıtıcı öğeleri ve kalabalığı kaldır, ana konuyu öne çıkar.' },
  { id: 'outpaint', label: 'Kadrajı Genişlet', prompt: 'Kadrajı çevreleyen alanı doğal ve uyumlu detaylarla genişlet (outpaint).' },
  { id: 'color_grade', label: 'Renk Paletini Değiştir', prompt: 'Renk paletini daha soğuk, film noir esintili ve kontrastlı hale getir.' },
] as const

export const STUDIO_MODES: StudioModeConfig[] = [
  {
    "id": "style_transfer",
    "label": "Voxnela Stilleri",
    "shortLabel": "Voxnela",
    "description": "Fotoğrafını sinematik, analog, retro veya tasarım DNA’sına aktar.",
    "iconName": "Palette",
    "coverUrl": "/styles/catalog/cinematic.webp",
    "accentColor": "#2563eb",
    "requiresUpload": false,
    "acceptedUploadKinds": [
      "image"
    ],
    "uploadHint": "İsteğe bağlı: Dönüştürülecek portre veya fotoğrafını ekle",
    "subcategories": [
      {
        "id": "cinematic",
        "mode": "style_transfer",
        "label": "Sinematik 35mm",
        "subtitle": "Derin, ölçülü ve sinema kalitesinde",
        "description": "35mm anamorfik lens derinliği, teal & orange renk derecelendirmesi ve doğal atmosferik aydınlatma.",
        "badge": "Popüler",
        "samplePrompt": "Yağmurlu bir akşamüstü neon ışıklı ıslak sokakta yürüyen melankolik karakter",
        "realPrompt": "Cinematic 35mm film still, anamorphic 2.39:1 widescreen, Kodak Vision3 500T 5219 film stock, atmospheric volumetric lighting, moody shallow depth of field, natural skin pores and micro-textures, subtle film grain and halation, muted teal and warm amber cinematic color grading, masterpiece photography.",
        "imageUrl": "/styles/catalog/cinematic.webp",
        "promptModifier": "Cinematic 35mm film still, anamorphic lens flare, Kodak Vision3 500T, subtle grain, shallow depth of field, natural skin texture, atmospheric haze",
        "tags": [
          "Sinema",
          "35mm",
          "Atmosfer"
        ]
      },
      {
        "id": "analog_film",
        "mode": "style_transfer",
        "label": "35mm Analog Film",
        "subtitle": "Sıcak Portra renkleri ve film greni",
        "description": "Kodak Portra 400 tonları, hafif ışık sızması ve organik gren dokusu.",
        "badge": "Trend",
        "samplePrompt": "Güneşli bir sahil kasabasında kahve içen genç bir kadın, vintage yaz havası",
        "realPrompt": "Authentic vintage 35mm film photography taken on Leica M6 with 50mm f/1.4 lens, Kodak Portra 400 color profile, golden hour soft natural side-lighting, organic film grain, warm nostalgic tones, subtle highlight glow and soft vignette.",
        "imageUrl": "/styles/catalog/analog_film.webp",
        "promptModifier": "Authentic 35mm film photography, Kodak Portra 400 tone, authentic film grain, slight halation on highlights, golden hour lighting, raw texture",
        "tags": [
          "Analog",
          "Portra",
          "Retro"
        ]
      },
      {
        "id": "editorial_fashion",
        "mode": "style_transfer",
        "label": "Vogue Editoryal Moda",
        "subtitle": "Stüdyo flaşı ve yüksek moda estetiği",
        "description": "Yüksek kontrastlı stüdyo flaşı, kusursuz kompozisyon ve editoryal moda duruşu.",
        "badge": "Lüks",
        "samplePrompt": "Minimalist mimari bir salonda heykelsi poz veren model, monokrom tonlar",
        "realPrompt": "High fashion editorial cover photography, Vogue Paris high-end aesthetics, direct hard studio flash with crisp geometric drop shadows, sculptural avant-garde pose, haute couture textured tailoring, flawless makeup with realistic skin texture, minimalist studio cyclorama.",
        "imageUrl": "/styles/catalog/editorial_fashion.webp",
        "promptModifier": "High fashion editorial photography, Vogue Paris aesthetic, direct studio flash, clean shadows, sharp focus, sculptural pose, haute couture styling",
        "tags": [
          "Moda",
          "Stüdyo",
          "Vogue"
        ]
      },
      {
        "id": "dark_cinematic",
        "mode": "style_transfer",
        "label": "Dark Sinematik",
        "subtitle": "Gölge oyunları ve derin kontrast",
        "description": "Chiaroscuro aydınlatma, derin siyahlar ve gizemli atmosfer.",
        "samplePrompt": "Gece yarısı sisli limanda bekleyen trenchcoat giymiş dedektif",
        "realPrompt": "Dark cinematic noir photography, master chiaroscuro lighting, deep rich obsidian blacks, razor-sharp rim lighting defining silhouettes, atmospheric fog and rain reflections, brooding psychological tension, Arri Alexa Mini LF film still.",
        "imageUrl": "/styles/catalog/dark_cinematic.webp",
        "promptModifier": "Dark cinematic photography, chiaroscuro lighting, deep rich blacks, moody atmosphere, subtle edge light, atmospheric mist",
        "tags": [
          "Noir",
          "Gerilim",
          "Gölge"
        ]
      },
      {
        "id": "vhs_camcorder",
        "mode": "style_transfer",
        "label": "90lar VHS Kaset",
        "subtitle": "Taramalar, renk kayması ve nostalji",
        "description": "Manyetik bant artefaktları, OSD zaman damgası ve retro renk tonları.",
        "samplePrompt": "1995 yazı bir kaykay parkında arkadaşlarla çekilmiş eğlenceli bir an",
        "realPrompt": "Authentic 1990s Panasonic analog VHS camcorder home video frame, magnetic tape tracking scanlines, slight phosphor chromatic aberration, glowing green OSD timestamp overlay in bottom corner \"AUG. 14 1996 08:42 PM\", nostalgic warm fluorescent glow, retro lo-fi aesthetic.",
        "imageUrl": "/styles/catalog/vhs_camcorder.webp",
        "promptModifier": "Authentic 1990s VHS home camcorder capture, analog tape tracking lines, chromatic aberration, timestamp OSD overlay in bottom corner, nostalgic warm glow",
        "tags": [
          "90lar",
          "VHS",
          "Nostalji"
        ]
      },
      {
        "id": "y2k_digital",
        "mode": "style_transfer",
        "label": "Y2K Dijital Flaş",
        "subtitle": "2000ler başı CCD sensör ve direkt flaş",
        "description": "Erken 2000ler kompakt dijital makine flaşı, parlak yansımalar ve canlı renkler.",
        "samplePrompt": "Gece kulübü girişinde gülümseyen arkadaş grubu, erken 2000ler sokak stili",
        "realPrompt": "Y2K early 2000s cyber party snapshot taken on Sony Cyber-shot compact CCD digital camera, direct harsh Xenon flash reflecting on metallic fabric, vibrant saturated pop colors, genuine vintage CCD noise, playful candid party energy.",
        "imageUrl": "/styles/catalog/y2k_digital.webp",
        "promptModifier": "Y2K early 2000s compact digicam look, direct harsh flash, CCD sensor noise, vibrant punchy colors, glossy highlights",
        "tags": [
          "Y2K",
          "Flaş",
          "Parti"
        ]
      },
      {
        "id": "glitch_cyber",
        "mode": "style_transfer",
        "label": "Glitch Cyberpunk",
        "subtitle": "RGB ayrışması ve neon siber doku",
        "description": "Holografik glitchler, RGB kayması ve distopik neo-şehir dokusu.",
        "samplePrompt": "Beni siberpunk fütüristik neon sokakta RGB glitch efektleriyle aydınlanan karizmatik bir kahraman yap",
        "realPrompt": "Futuristic cyberpunk portrait photography. Charismatic subject styled in high-tech cybernetic jacket, holographic neon cyan and hot magenta HUD interface fragmentation, RGB channel displacement, rain-slicked neon street reflections, high-tech dystopian atmosphere.",
        "imageUrl": "/styles/catalog/glitch_cyber.webp",
        "promptModifier": "Cyberpunk glitch portrait, RGB split chromatic aberration, holographic interface reflections, neon magenta and cyan rain reflections, strict subject preservation",
        "tags": [
          "Siberpunk",
          "Glitch",
          "Neon"
        ]
      },
      {
        "id": "collage_zine",
        "mode": "style_transfer",
        "label": "Punk Risograph Zine",
        "subtitle": "Yırtık kağıt, fotokopi ve iki renkli baskı",
        "description": "Ham fanzine dokusu, yarım ton (halftone) tramlar ve kolaj katmanları.",
        "samplePrompt": "Beni iki renkli fotokopi dokusu ve tram noktalarıyla bağımsız bir punk fanzin portresi yap",
        "realPrompt": "Underground punk fanzine risograph art print portrait. Stylized subject portrait printed with tactile screen-printed halftone dot raster, torn textured kraft paper edges, high-contrast black and fluorescent crimson duotone ink, raw DIY photocopier collage aesthetic.",
        "imageUrl": "/styles/catalog/collage_zine.webp",
        "promptModifier": "Risograph zine portrait aesthetic, halftone dot screen printing, torn paper edges, tactile rough photocopy texture, duotone ink bleed",
        "tags": [
          "Zine",
          "Kolaj",
          "Punk"
        ]
      }
    ]
  },
  {
    "id": "virtual_tryon",
    "label": "Sanal Giyinme Odası & Kıyafet Deneme",
    "shortLabel": "Giyinme Odası",
    "description": "Kendi fotoğrafına dilediğin stili, ceketi, takım elbiseyi veya sokak modasını giydir.",
    "iconName": "Shirt",
    "coverUrl": "/styles/catalog/streetwear_oversized.webp",
    "accentColor": "#10b981",
    "requiresUpload": true,
    "acceptedUploadKinds": [
      "image"
    ],
    "uploadHint": "Kıyafet denemek istediğin boydan veya yarım boy portre fotoğrafını yükle",
    "subcategories": [
      {
        "id": "streetwear_oversized",
        "mode": "virtual_tryon",
        "label": "Sokak Modası & Streetwear",
        "subtitle": "Oversized hoodie, kargo pantolon ve retro sneaker",
        "description": "Yüzü ve saç yapısını bozmadan sokak stiline dönüştür: modern Tokyo ve Seul modası, hacimli bomber ceket, katmanlı kapüşonlu üst.",
        "badge": "Face-Lock",
        "samplePrompt": "Modern oversized kargo pantolon, kalın taban retro sneaker ve kapüşonlu tasarım bomber ceket",
        "realPrompt": "Full body street fashion editorial photography, trendy model wearing oversized cyberpunk techwear multi-pocket cargo pants, heavy olive green designer bomber jacket, chunky Balenciaga-style sneakers, Shibuya Tokyo alleyway backdrop at dusk, dynamic neon lighting, photorealistic 8k.",
        "imageUrl": "/styles/catalog/streetwear_oversized.webp",
        "promptModifier": "Wearing contemporary Tokyo streetwear, high-end oversized hoodie under boxy nylon bomber jacket, technical cargo pants, limited edition sneakers, editorial street lighting, strict face preservation",
        "tags": [
          "Streetwear",
          "Oversized",
          "Sneaker",
          "Face-Lock"
        ]
      },
      {
        "id": "haute_couture",
        "mode": "virtual_tryon",
        "label": "Haute Couture & Kırmızı Halı",
        "subtitle": "Lüks gece elbisesi veya terzi dikimi smokin",
        "description": "Yüz hatlarını koruyarak Milano ve Paris podyum podyum kıyafeti: saten, ipek veya kadife lüks gece kıyafeti.",
        "badge": "Face-Lock",
        "samplePrompt": "Koyu zümrüt yeşili saten omuz dekolteli gece elbisesi, altın mücevher detayları",
        "realPrompt": "Haute couture red carpet fashion photography, stunning model on the Cannes film festival red carpet wearing an emerald green sculpted satin silk evening gown with handcrafted gold embroidery, direct paparazzi flash, ultra-luxurious editorial.",
        "imageUrl": "/styles/catalog/haute_couture.webp",
        "promptModifier": "Dressed in bespoke haute couture red carpet attire, luxurious silk satin fabric, precision tailoring, elegant silhouette, high jewelry accents, gala lighting, strict face preservation",
        "tags": [
          "Kırmızı Halı",
          "Smokin",
          "Gece Elbisesi",
          "Face-Lock"
        ]
      },
      {
        "id": "vintage_leather",
        "mode": "virtual_tryon",
        "label": "Vintage Deri & Biker Ceket",
        "subtitle": "70ler eskitme deri mont ve rock şıklığı",
        "description": "Yüz ifadesini değiştirmeden doğal yıpranmış dokulu siyah veya taba deri ceket ve klasik beyaz tişört.",
        "badge": "Face-Lock",
        "samplePrompt": "Yıpranmış dokulu klasik kahverengi deri pilot ceketi ve siyah boğazlı kazak",
        "realPrompt": "Vintage cafe racer motorcycle portrait, handsome rugged individual wearing a weathered patinated dark brown cowhide leather biker jacket with antique brass zippers, standing next to a classic Triumph motorcycle, sunset golden flare, Kodak Portra aesthetic.",
        "imageUrl": "/styles/catalog/vintage_leather.webp",
        "promptModifier": "Wearing a vintage distressed brown shearling leather aviator jacket, fitted black cashmere turtleneck, timeless heritage style, cinematic daylight, strict face preservation",
        "tags": [
          "Deri Ceket",
          "Vintage",
          "Biker",
          "Face-Lock"
        ]
      },
      {
        "id": "seasonal_outerwear",
        "mode": "virtual_tryon",
        "label": "Sezonluk Şık & Trençkot",
        "subtitle": "Klasik bej trençkot veya kışlık lüks kaşe kaban",
        "description": "Yüzü ve kimliği koruyarak zamansız sonbahar/kış stili: çift düğmeli trençkot, yün atkı ve minimalist ayakkabılar.",
        "badge": "Face-Lock",
        "samplePrompt": "Zarif bej çift düğmeli uzun trençkot ve ince yün fular",
        "realPrompt": "Autumn Parisian street style editorial, elegant model in a structured double-breasted camel cashmere trench coat with tied belt, navy silk neck scarf, strolling on wet cobblestones strewn with golden maple leaves, soft diffused daylight.",
        "imageUrl": "/styles/catalog/seasonal_outerwear.webp",
        "promptModifier": "Wearing a classic tailored camel double-breasted trench coat, fine merino wool scarf, sharp minimalist city styling, soft overcast daylight, strict face preservation",
        "tags": [
          "Trençkot",
          "Kaban",
          "Sonbahar",
          "Face-Lock"
        ]
      },
      {
        "id": "period_costume",
        "mode": "virtual_tryon",
        "label": "Tarihi Kostüm & Dönem Kıyafeti",
        "subtitle": "1920ler Peaky yelekli takım veya Viktorya zarafeti",
        "description": "Yüzü tam koruyarak tarihsel dikiş detayları, tüvit yelek, cep saati kösteği veya fırfırlı dönem elbisesi.",
        "badge": "Face-Lock",
        "samplePrompt": "1920ler Birmingham tarzı tüvit yelekli takım elbise, cep saati zinciri ve kasket",
        "realPrompt": "1920s Birmingham Peaky Blinders aesthetic historical portrait, sharp character wearing a tailored charcoal herringbone three-piece tweed suit, gold pocket watch chain, newsboy flat cap, moody industrial brick alleyway, cinematic chiaroscuro.",
        "imageUrl": "/styles/catalog/period_costume.webp",
        "promptModifier": "Dressed in authentic 1920s heritage tailored three-piece tweed suit with pocket watch chain and newsboy cap, rich woolen textures, period drama lighting, strict face preservation",
        "tags": [
          "1920ler",
          "Tarihi",
          "Peaky",
          "Face-Lock"
        ]
      }
    ]
  },
  {
    "id": "magazine_cover",
    "label": "Dergi Kapağı & Tarihi Gazete",
    "shortLabel": "Dergi & Gazete",
    "description": "Fotoğrafını Vogue, Time/Forbes kapağına veya 1920ler gazete manşetine dönüştür.",
    "iconName": "BookOpen",
    "coverUrl": "/styles/catalog/forbes_person_of_year.webp",
    "accentColor": "#ec4899",
    "requiresUpload": false,
    "acceptedUploadKinds": [
      "image"
    ],
    "uploadHint": "İsteğe bağlı: Kapakta yer almasını istediğin portreyi yükle",
    "subcategories": [
      {
        "id": "vogue_fashion_cover",
        "mode": "magazine_cover",
        "label": "Vogue & Yüksek Moda Kapağı",
        "subtitle": "İmza editoryal serif başlık, alt başlıklar ve barkod",
        "description": "Vogue dergi kapağı formatı: büyük sans serif/serif logo arkasında duran model, editoryal moda manşetleri ve lüks mizanpaj.",
        "badge": "İkonik",
        "samplePrompt": "Güneş gözlüğü ve şık ceketle kameraya bakan karizmatik model, \"THE NEW ERA OF STYLE\" başlığı",
        "realPrompt": "Iconic high-fashion magazine cover layout, supermodel posing with commanding gaze, bold masthead typography \"VOGUE\" across top, bespoke designer haute couture, direct ring light with clean rim illumination, glossy luxury editorial finish.",
        "imageUrl": "/styles/catalog/vogue_fashion_cover.webp",
        "promptModifier": "Iconic fashion magazine cover design, large bold editorial masthead typography, headline callouts, professional studio lighting, high gloss luxury print look",
        "tags": [
          "Vogue",
          "Kapak",
          "Moda"
        ]
      },
      {
        "id": "forbes_person_of_year",
        "mode": "magazine_cover",
        "label": "Time / Forbes \"Yılın Kişisi\"",
        "subtitle": "Kırmızı çerçeve, prestijli liderlik manşeti",
        "description": "Time kırmızı kenar çerçevesi veya Forbes siyah-altın kapak dizilimi: güçlü liderlik, inovasyon ve vizyon temalı tipografi.",
        "badge": "Prestij",
        "samplePrompt": "Düşünceli ve özgüvenli bir lider portresi, \"PERSON OF THE YEAR: THE INNOVATOR\" manşeti",
        "realPrompt": "Prestige business magazine cover layout, visionary leader portrait with confident composed expression in tailored dark blazer, bold typography headline \"FORBES PERSON OF THE YEAR: THE ARCHITECT OF AI\", studio rim lighting, premium editorial aesthetic.",
        "imageUrl": "/styles/catalog/forbes_person_of_year.webp",
        "promptModifier": "Time Person of the Year magazine cover, bold red iconic frame border, serif cover typography, intense leadership portrait, dramatic studio rim light",
        "tags": [
          "Forbes",
          "Time",
          "Liderlik"
        ]
      },
      {
        "id": "vintage_broadsheet_1920",
        "mode": "magazine_cover",
        "label": "1920s Tarihi Gazete Sayfası",
        "subtitle": "Sararmış kağıt, gravür baskı ve gotik başlık",
        "description": "1920lerin tarihi New York veya Londra gazetesi ön sayfası: Gotik gazete logosu, \"SON DAKİKA\" manşeti, çok sütunlu antika metin ve sepya görsel.",
        "badge": "Tarihi",
        "samplePrompt": "Büyük keşif manşeti: \"MYSTERIOUS EXPEDITION RETURNS TO CAPITAL\", eskitme gazete fotoğrafı",
        "realPrompt": "Authentic 1920s vintage broadsheet front page newspaper, sepia toned aged paper texture with fold creases, bold historic serif headline \"HISTORIC EXPEDITION DISCOVERS ANCIENT TOMB\", antique black and white halftone photograph, multi-column print, museum archival artifact.",
        "imageUrl": "/styles/catalog/vintage_broadsheet_1920.webp",
        "promptModifier": "1920s historical newspaper front page, aged yellowed broadsheet paper texture, gothic blackletter newspaper title, dramatic halftone b&w photo, authentic printing press artifacts",
        "tags": [
          "Gazete",
          "1920ler",
          "Antika"
        ]
      },
      {
        "id": "vinyl_record_sleeve",
        "mode": "magazine_cover",
        "label": "Plak & Albüm Kapağı (Vinyl Sleeve)",
        "subtitle": "Kare 1:1 format, karton aşınması ve plak halkası",
        "description": "Gerçek bir vinil plak kapağı simülasyonu: dairesel plak izi aşınması (ring wear), indie müzik tipografisi ve sanatsal kapak kompozisyonu.",
        "badge": "Müzik",
        "samplePrompt": "Lo-fi indie rock albüm kapağı, melankolik gün batımı ve retro fontla grup ismi",
        "realPrompt": "12-inch vinyl record album cover artwork, indie psych-rock aesthetic, surreal sunset landscape with grain and tactile cardboard sleeve wear, artistic typography with album title, retro analog color palette, gatefold sleeve design.",
        "imageUrl": "/styles/catalog/vinyl_record_sleeve.webp",
        "promptModifier": "12-inch vinyl LP record jacket, subtle circular vinyl ring wear impression on cardboard sleeve, indie record label typography, artistic album artwork, tactile analog finish",
        "tags": [
          "Plak",
          "Albüm",
          "Vinyl"
        ]
      }
    ]
  },
  {
    "id": "poster_studio",
    "label": "Afiş & Tipografi Stüdyosu",
    "shortLabel": "Poster Stüdyosu",
    "description": "A24 sinema afişi, İsviçre modernizm sergi posteri ve festival duyuruları tasarla.",
    "iconName": "Image",
    "coverUrl": "/styles/catalog/a24_cinema_onesheet.webp",
    "accentColor": "#f59e0b",
    "requiresUpload": false,
    "acceptedUploadKinds": [
      "image"
    ],
    "uploadHint": "İsteğe bağlı: Postere entegre edilecek ana görseli veya logoyu ekle",
    "subcategories": [
      {
        "id": "a24_cinema_onesheet",
        "mode": "poster_studio",
        "label": "A24 Sinema Film Afişi",
        "subtitle": "Festival ödül defnesi, alt billing bloğu ve sinematik sadelik",
        "description": "Cannes/Sundance ödüllü A24 filmleri tarzı: Güçlü merkezi görsel, minimal zarif serif başlık, yönetmen adı ve resmi sinema afişi dipnotları.",
        "badge": "Sinema",
        "samplePrompt": "Sessiz bir göl kıyısında tek başına duran bir figür, \"ECHOES OF SILENCE\" film başlığı",
        "realPrompt": "Award-winning A24 indie cinema movie poster, dramatic solitary figure standing on foggy lake shore surrounded by pine trees, moody atmospheric mist, minimalist elegant serif title typography \"ECHOES OF SILENCE\", film festival laurels at top, 35mm film still.",
        "imageUrl": "/styles/catalog/a24_cinema_onesheet.webp",
        "promptModifier": "A24 indie movie poster design, festival laurels, award winning typography, official movie billing credit block at bottom, atmospheric cinematic key visual",
        "tags": [
          "A24",
          "Film Afişi",
          "Sinema"
        ]
      },
      {
        "id": "swiss_typography_poster",
        "mode": "poster_studio",
        "label": "İsviçre Tipografik Sergi Posteri",
        "subtitle": "Akzidenz/Helvetica ızgara sistemi, asimetrik kırmızı-siyah denge",
        "description": "Zürih ve Basel tasarım okulu mirası: Asimetrik matematiksel ızgara, büyük punto tipografi, geometrik formlar ve tavizsiz modernizm.",
        "badge": "Minimal",
        "samplePrompt": "Bauhaus ve İsviçre modernizm sergisi için kırmızı-siyah tipografik geometri",
        "realPrompt": "Swiss Style International Typographic Style exhibition poster, minimalist stark composition with bold red and black geometric diagonal bars, asymmetric rigid grid layout, clean Helvetica typography, Bauhaus influence, museum graphic design.",
        "imageUrl": "/styles/catalog/swiss_typography_poster.webp",
        "promptModifier": "International Typographic Style, Swiss poster design, strict modular grid, bold Helvetica typography, high contrast red and black geometry, museum exhibition poster",
        "tags": [
          "Swiss",
          "Helvetica",
          "Bauhaus"
        ]
      },
      {
        "id": "festival_music_lineup",
        "mode": "poster_studio",
        "label": "Müzik Festivali & Konser Afişi",
        "subtitle": "Sanatçı kadrosu, neon dalgalar ve kinetik tipografi",
        "description": "Elektronik müzik festivali veya rock konseri line-up posteri: headliner isimleri, tarih/mekan detayları ve dinamik görsel enerji.",
        "badge": "Canlı",
        "samplePrompt": "Çölde gün batımı elektronik müzik festivali lineup posteri",
        "realPrompt": "Electronic music festival lineup poster, desert dusk setting with giant glowing neon geometric stage installation, kinetic festival typography with artist lineup, vibrant psychedelic gradients, high-impact festival graphic art.",
        "imageUrl": "/styles/catalog/festival_music_lineup.webp",
        "promptModifier": "Music festival lineup poster, psychedelic kinetic typography, gradient neon color palette, layered artist roster text, vibrant concert aesthetic",
        "tags": [
          "Festival",
          "Konser",
          "Müzik"
        ]
      },
      {
        "id": "retro_travel_lithograph",
        "mode": "poster_studio",
        "label": "Retro Seyahat & Turizm Afişi",
        "subtitle": "1950ler taşbaskı renkleri ve nostaljik kaçış rotaları",
        "description": "50ler ve 60lar havayolu ve turizm afişi: İpek baskı renk geçişleri, temiz illüstrasyon dili ve nostaljik şehir tanıtım tipografisi.",
        "badge": "Vintage",
        "samplePrompt": "Kapadokya peri bacaları üzerinde yükselen sıcak hava balonları seyahat afişi",
        "realPrompt": "1950s vintage travel tourism lithograph poster, Cappadocia fairy chimneys with colorful hot air balloons rising into vibrant pastel sunrise sky, bold hand-lettered tourism typography \"VISIT CAPPADOCIA\", mid-century silkscreen art style.",
        "imageUrl": "/styles/catalog/retro_travel_lithograph.webp",
        "promptModifier": "1950s vintage airline travel lithograph poster, mid-century modern illustration style, pastel serigraph color palette, hand-lettered tourism typography",
        "tags": [
          "Seyahat",
          "Turizm",
          "Retro"
        ]
      }
    ]
  },
  {
    "id": "infographic",
    "label": "İnfografik & Veri Görselleştirme",
    "shortLabel": "İnfografik",
    "description": "Karmaşık fikirleri Vox/Netflix explainer şemalarına ve izometrik teknik kesitlere dönüştür.",
    "iconName": "BarChart3",
    "coverUrl": "/styles/catalog/comparison_matrix.webp",
    "accentColor": "#06b6d4",
    "requiresUpload": false,
    "acceptedUploadKinds": [
      "document",
      "image"
    ],
    "uploadHint": "İsteğe bağlı: Veri tablosu, taslak şema veya metin özeti ekle",
    "subcategories": [
      {
        "id": "vox_explainer_diagram",
        "mode": "infographic",
        "label": "Vox / Netflix Explainer Şeması",
        "subtitle": "Koyu zemin üzerinde parlayan minimalist veri akışı ve oklar",
        "description": "Koyu antrasit zemin, canlı neon oklar, yüzde balonları ve anlaşılır adımlarla açıklayıcı bir görsel.",
        "badge": "Popüler",
        "samplePrompt": "Yapay zekanın çalışma mantığını gösteren nöral ağ ve veri akışı şeması",
        "realPrompt": "High-end Vox style motion graphic explainer infographic, deep slate background with luminous cyan and coral vector diagrams explaining neural network data processing, clean sans-serif typography, clean visual hierarchy, editorial data story.",
        "imageUrl": "/styles/catalog/vox_explainer_diagram.webp",
        "promptModifier": "Vox media explainer graphic style, dark matte background, bright vector highlight arrows, step-by-step numbered callouts, sleek educational infographic",
        "tags": [
          "Vox",
          "Explainer",
          "Eğitim"
        ]
      },
      {
        "id": "chronological_timeline",
        "mode": "infographic",
        "label": "Zaman Çizelgesi & Yol Haritası (Timeline)",
        "subtitle": "Tarihsel kilometre taşları, dikey veya yatay akış",
        "description": "Şirket tarihi, ürün yol haritası (roadmap) veya tarihsel bir olayın kronolojisi: temiz düğüm noktaları ve açıklama kartları.",
        "badge": "Yol Haritası",
        "samplePrompt": "Uzay yarışının 1957-1969 arası önemli dönüm noktalarını anlatan zaman çizelgesi",
        "realPrompt": "Executive chronological timeline roadmap poster, sleek horizontal milestone trajectory from 1950 to 2050, glowing milestone nodes, clean callout cards with icons and statistics, minimalist Swiss data design, dark UI theme.",
        "imageUrl": "/styles/catalog/chronological_timeline.webp",
        "promptModifier": "Clean chronological timeline infographic, milestone markers on glowing trajectory line, year badges, concise historical event callout cards, technical clarity",
        "tags": [
          "Timeline",
          "Kronoloji",
          "Tarih"
        ]
      },
      {
        "id": "isometric_blueprint",
        "mode": "infographic",
        "label": "İzometrik Blueprint & Teknik Kesit",
        "subtitle": "Mühendislik blueprinti, patlatılmış mekanik montaj",
        "description": "Mimari veya endüstriyel patlatılmış ürün kesiti: Mavi ozalit/blueprint ızgarası, 3D izometrik çizim ve teknik parça numaralandırmaları.",
        "badge": "Teknik",
        "samplePrompt": "Elektrikli bir otomobil bataryasının ve motorunun patlatılmış teknik kesit blueprint çizimi",
        "realPrompt": "Technical isometric 3D blueprint exploded view, precision CAD wireframe and solid cutaway of an advanced electric vehicle battery and motor powertrain, glowing technical callouts, cyan blueprint grid, architectural drafting aesthetic.",
        "imageUrl": "/styles/catalog/isometric_blueprint.webp",
        "promptModifier": "Technical isometric exploded view blueprint, architectural cyan grid background, CAD vector linework, precise dimensional callouts and labels, engineering illustration",
        "tags": [
          "Blueprint",
          "İzometrik",
          "Teknik"
        ]
      },
      {
        "id": "comparison_matrix",
        "mode": "infographic",
        "label": "Karşılaştırma & Karar Matrisi",
        "subtitle": "İki yaklaşımı veya ürünü yan yana kıyaslayan profesyonel tablo",
        "description": "A vs B kıyaslaması: Avantajlar, dezavantajlar, hız, maliyet ve performans kriterlerini net simgeler ve yüzdelerle gösteren infografik.",
        "badge": "Karşılaştırma",
        "samplePrompt": "Yenilenebilir enerji kaynakları vs fosil yakıtların verimlilik karşılaştırması",
        "realPrompt": "Modern tech comparison matrix infographic poster, clean minimalist layout comparing Cloud AI vs Edge AI, glowing cyan and amber data nodes, elegant metrics typography, dark mode UI visualization, vector graphic precision.",
        "imageUrl": "/styles/catalog/comparison_matrix.webp",
        "promptModifier": "Side-by-side comparison matrix graphic, two-column contrast layout, checkmarks and cross icons, metric comparison bars, clean decision matrix infographic",
        "tags": [
          "Karşılaştırma",
          "Matris",
          "Analiz"
        ]
      }
    ]
  },
  {
    "id": "time_warp",
    "label": "Zaman & Mekan Işınlayıcı",
    "shortLabel": "Zaman Işınlayıcı",
    "description": "Kendini Jura devrine, Buzul çağına, 1920ler caz kulübüne veya 2077ye ışınla.",
    "iconName": "Compass",
    "coverUrl": "/styles/catalog/prehistoric_jurassic.webp",
    "accentColor": "#8b5cf6",
    "requiresUpload": false,
    "acceptedUploadKinds": [
      "image"
    ],
    "uploadHint": "İsteğe bağlı: Işınlanacak kişinin portresini ekle (yüz korunur)",
    "subcategories": [
      {
        "id": "prehistoric_jurassic",
        "mode": "time_warp",
        "label": "Tarih Öncesi & Dinozorlar Çağı",
        "subtitle": "Dev eğrelti otları, volkanik duman ve Jura florası",
        "description": "150 milyon yıl öncesine yolculuk: Nemli devasa eğrelti ormanı, arka planda sisler içindeki otobur dinozor siluetleri ve ilkel doğa.",
        "badge": "Jura",
        "samplePrompt": "Jurassic dev eğrelti ormanında antik güneş ışıkları altında duran karakter",
        "realPrompt": "Cinematic film still of a prehistoric Jurassic rainforest, towering giant prehistoric ferns and morning mist, a majestic Brachiosaurus walking in the distance through sunbeams filtering through the canopy, National Geographic documentary realism, 8k.",
        "imageUrl": "/styles/catalog/prehistoric_jurassic.webp",
        "promptModifier": "Prehistoric Jurassic jungle environment, towering ancient cycads and giant ferns, distant sauropod dinosaur silhouette, humid volumetric sun rays through canopy, documentary film still",
        "tags": [
          "Jura",
          "Dinozor",
          "Tarih Öncesi"
        ]
      },
      {
        "id": "prehistoric_ice_age",
        "mode": "time_warp",
        "label": "Buzul Devri & Mamut Seferi",
        "subtitle": "Buzullar, donmuş göller ve mamut sürüleri",
        "description": "Pleistosen buzul çağı tundrası: Kürk başlık, tipi fırtınası, dev yünlü mamutlar ve donmuş dağ geçitleri.",
        "badge": "Buzul",
        "samplePrompt": "Karlı dağ yamacında dev yünlü mamutların yanında duran kürk başlıklı karakter",
        "realPrompt": "Cinematic Ice Age landscape, vast snow-covered tundra with towering ancient glaciers, a magnificent herd of woolly mammoths with curved tusks migrating across frozen terrain during a swirling snow blizzard, golden arctic twilight sun, epic realism, 8k.",
        "imageUrl": "/styles/catalog/prehistoric_ice_age.webp",
        "promptModifier": "Pleistocene Ice Age tundra, towering glacial walls, blizzard snow storm, woolly mammoth herd with massive tusks, cinematic arctic survival aesthetic",
        "tags": [
          "Buzul",
          "Mamut",
          "Kutup"
        ]
      },
      {
        "id": "prehistoric_stone_age",
        "mode": "time_warp",
        "label": "Taş Devri & İlkel Mağara Ateşi",
        "subtitle": "Mağara resimleri, meşale aydınlatması ve taş aletler",
        "description": "Paleolitik mağara içi: Duvarlardaki aşıboyası bizon çizimleri, titreşen ateş ışığı, meşale ve otantik ilkel yaşam sahnesi.",
        "badge": "Paleolitik",
        "samplePrompt": "Mağara resimleri önünde elinde meşaleyle duran tarih öncesi kıyafetli karakter",
        "realPrompt": "Atmospheric Stone Age cave scene, primitive hunter wearing fur garments holding a glowing torch illuminating ancient primitive cave paintings of bison and horses on rugged stone walls, floating embers, warm flickering firelight, dramatic realism.",
        "imageUrl": "/styles/catalog/prehistoric_stone_age.webp",
        "promptModifier": "Stone age cave interior, authentic prehistoric cave wall paintings, flickering warm firelight illumination, smoke and floating sparks, raw prehistoric authenticity",
        "tags": [
          "Taş Devri",
          "Mağara",
          "Ateş"
        ]
      },
      {
        "id": "paris_1920_jazz",
        "mode": "time_warp",
        "label": "1920ler Paris Caz Kulübü",
        "subtitle": "Loş pirinç lambalar, duman altı bar ve caz piyanisti",
        "description": "Altın 1920ler Montparnasse yeraltı kulübü: Smokinler, charleston elbiseleri, art deco bar detayları ve sıcak caz aydınlatması.",
        "badge": "1920ler",
        "samplePrompt": "1920ler Parisinde loş bir caz kulübünde piyano başında içkisini yudumlayan şık figür",
        "realPrompt": "1920s Parisian underground jazz club, smoky speakeasy atmosphere, a saxophonist and pianist performing in warm sepia spotlight, glamorous patrons in flapper dresses and tuxedos enjoying vintage cocktails, art deco interior, analog film still.",
        "imageUrl": "/styles/catalog/paris_1920_jazz.webp",
        "promptModifier": "1920s Paris speakeasy jazz club, smoky warm incandescent ambiance, art deco interior, flapper dress and tuxedo patrons, vintage sepia tone lighting",
        "tags": [
          "1920ler",
          "Paris",
          "Caz"
        ]
      },
      {
        "id": "america_1950_kodachrome",
        "mode": "time_warp",
        "label": "1950ler Amerika & Kodachrome",
        "subtitle": "Neon diner, klasik kuyruklu otomobiller ve canlı renkler",
        "description": "1950lerin sıcak Amerikan rüyası: Route 66 diner restoranı, kırmızı vinil koltuklar, krom detaylar ve canlı Kodachrome 64 renk doygunluğu.",
        "badge": "1950ler",
        "samplePrompt": "1950ler Route 66 üzerindeki bir diner restoranda milkshake içen genç figür",
        "realPrompt": "Authentic 1950s American Kodachrome photograph, a vintage classic diner on Route 66 at twilight with glowing neon signs, a turquoise Chevrolet convertible parked outside, nostalgic rich warm colors, 35mm film still.",
        "imageUrl": "/styles/catalog/america_1950_kodachrome.webp",
        "promptModifier": "Authentic 1950s Kodachrome slide photography, retro American diner setting, chrome tailfin automobile in background, saturated primary colors, warm mid-century nostalgic glow",
        "tags": [
          "1950ler",
          "Diner",
          "Kodachrome"
        ]
      },
      {
        "id": "miami_1980_synthwave",
        "mode": "time_warp",
        "label": "1980ler Miami & Synthwave",
        "subtitle": "Palmiyeler, pembe-mor gün batımı ve retro spor araba",
        "description": "1980lerin retro dalgası: Miami Ocean Drive sahil şeridi, pembe ve turkuaz gökyüzü, beyaz spor araba ve dönemin ikonik estetiği.",
        "badge": "1980ler",
        "samplePrompt": "1980ler Miami sahilinde gün batımında spor arabanın yanında duran figür",
        "realPrompt": "1980s Miami Vice aesthetic, sleek white sports car parked by palm trees along Ocean Drive, vibrant pastel pink and cyan sunset sky, neon art deco hotel reflections, retro 80s synthwave vibe, 35mm film photography.",
        "imageUrl": "/styles/catalog/miami_1980_synthwave.webp",
        "promptModifier": "1980s Miami aesthetic, vibrant pastel pink and neon cyan sunset sky, silhouette palm trees along Ocean Drive, retro white sports coupe, 80s film aesthetic",
        "tags": [
          "1980ler",
          "Miami",
          "Synthwave"
        ]
      },
      {
        "id": "neotokyo_2077_cyberpunk",
        "mode": "time_warp",
        "label": "2077 Neo-Tokyo Siber Gelecek",
        "subtitle": "Uçan araçlar, dikey gökdelenler ve sibernetik yansımalar",
        "description": "Distopik ve büyüleyici 2077 metropolü: Yağmurla parlayan asma köprüler, dev hologram reklamlar ve sibernetik sokak kültürü.",
        "badge": "2077",
        "samplePrompt": "2077 Neo-Tokyo yağmurlu üst geçidinde duran figür, parlayan sibernetik kol yansıması",
        "realPrompt": "Futuristic year 2077 Neo-Tokyo cyberpunk street scene, towering holographic billboards illuminating rain-slicked neon streets, flying spinner vehicles between skyscrapers, dense futuristic sci-fi city atmosphere.",
        "imageUrl": "/styles/catalog/neotokyo_2077_cyberpunk.webp",
        "promptModifier": "Futuristic year 2077 Neo-Tokyo megalopolis, flying spinner cars between hyper-skyscrapers, massive holographic advertising in rain mist, high-tech dystopian world",
        "tags": [
          "2077",
          "Siber",
          "Gelecek"
        ]
      }
    ]
  },
  {
    "id": "commercial",
    "label": "Ticari Ürün & Reklam Vitrini",
    "shortLabel": "Ürün Vitrini",
    "description": "Ürününü Apple Keynote kaidesine, MacBook/iPhone ekranına veya lüks mermer kaideye yerleştir.",
    "iconName": "ShoppingBag",
    "coverUrl": "/styles/catalog/commercial_product.webp",
    "accentColor": "#e11d48",
    "requiresUpload": true,
    "acceptedUploadKinds": [
      "image"
    ],
    "uploadHint": "Vitrinde sergilemek istediğin ürünün veya ekran görüntünün fotoğrafını yükle",
    "subcategories": [
      {
        "id": "macbook_screen_mockup",
        "mode": "commercial",
        "label": "MacBook Pro Ekran Mockup",
        "subtitle": "Yüklenen arayüzü açık MacBook ekranına perspektifle giydir",
        "description": "Uygulama veya web sitenin ekran görüntüsünü modern bir çalışma masasında açık duran MacBook Pro ekranına kusursuz perspektif ve cam yansımalarıyla entegre et.",
        "badge": "Mockup",
        "samplePrompt": "Açık duran MacBook Pro ekranına yüklenen SaaS kontrol paneli arayüzünü tam perspektifle yerleştir",
        "realPrompt": "Clean professional studio commercial mockup, space grey MacBook Pro open on modern wooden desk displaying a sleek dark analytics SaaS dashboard on screen, soft morning window light, potted plant, minimalist aesthetic.",
        "imageUrl": "/styles/catalog/macbook_screen_mockup.webp",
        "promptModifier": "Photorealistic MacBook Pro studio mockup, screen displaying provided software interface with accurate perspective and glass gloss, modern minimal designer desk background, soft natural lighting",
        "tags": [
          "MacBook",
          "Mockup",
          "SaaS",
          "UI"
        ]
      },
      {
        "id": "iphone_screen_mockup",
        "mode": "commercial",
        "label": "iPhone 16 Pro Ekran Mockup",
        "subtitle": "Mobil uygulamayı titanyum çerçeveli iPhone ekranına aktar",
        "description": "Mobil uygulama tasarımını zarifçe havada süzülen veya elde duran iPhone 16 Pro ekranına yansıt: Dinamik Ada uyumlu, OLED siyahları ve stüdyo aydınlatması.",
        "badge": "Mockup",
        "samplePrompt": "iPhone 16 Pro ekranına mobil e-ticaret uygulaması tasarımını yerleştir",
        "realPrompt": "Premium product showcase, titanium iPhone 16 Pro floating elegantly at a 45 degree angle displaying a vibrant banking app interface on screen, studio lighting with soft specular highlights, clean dark gradient backdrop.",
        "imageUrl": "/styles/catalog/iphone_screen_mockup.webp",
        "promptModifier": "Titanium iPhone 16 Pro commercial device mockup, mobile UI design mapped seamlessly on edge-to-edge screen, elegant studio rim lighting, crisp 8k product render",
        "tags": [
          "iPhone",
          "Mobil",
          "Mockup",
          "App"
        ]
      },
      {
        "id": "apple_ecosystem_mockup",
        "mode": "commercial",
        "label": "Apple Cihaz Ailesi Mockup",
        "subtitle": "MacBook, iPad ve iPhone yan yana senkronize vitrin",
        "description": "Çoklu platform dijital ürünler için: MacBook Pro, iPad Pro ve iPhone 16 Pro aynı masada, ürünün duyarlı tasarımını (responsive) aynı anda sergiler.",
        "badge": "Eko-sistem",
        "samplePrompt": "Masaüstünde MacBook, iPad ve iPhone üzerinde senkronize görünen uygulama tasarımı",
        "realPrompt": "Apple ecosystem commercial mockup, MacBook Pro, iPad Pro with Apple Pencil, and iPhone seamlessly arranged on designer marble desk, showing synchronized creative studio workflow across screens, architectural lighting.",
        "imageUrl": "/styles/catalog/apple_ecosystem_mockup.webp",
        "promptModifier": "Apple hardware ecosystem commercial shot, MacBook Pro, iPad Pro with stylus and iPhone arranged harmoniously, clean product workspace, premium tech marketing presentation",
        "tags": [
          "Apple",
          "Ecosystem",
          "Responsive",
          "Mockup"
        ]
      },
      {
        "id": "apple_keynote_pedestal",
        "mode": "commercial",
        "label": "Apple Keynote Kaidesi",
        "subtitle": "Mat gri podyum, yumuşak tepe ışığı ve minimal sadelik",
        "description": "Apple lansman etkinliği tarzı: Mat kireçtaşı silindirik kaide üzerinde duran ürün, nötr gri stüdyo fonu ve yumuşak temas gölgesi.",
        "badge": "Keynote",
        "samplePrompt": "Mat taş kaide üzerinde duran minimal ürün, yumuşak stüdyo üst ışığı ve gölgesi",
        "realPrompt": "Ultra-minimalist Apple Keynote product launch stage, single cylindrical matte limestone pedestal centered in soft studio light, seamless light gray cyclorama background, subtle soft contact shadow, premium industrial product showcase.",
        "imageUrl": "/styles/catalog/apple_keynote_pedestal.webp",
        "promptModifier": "Apple product keynote stage aesthetic, minimalist matte cylindrical stone pedestal, soft overhead diffused spot light, seamless neutral gray studio cyclorama, crisp industrial design presentation",
        "tags": [
          "Apple",
          "Kaide",
          "Ürün",
          "3D"
        ]
      },
      {
        "id": "luxury_wet_marble",
        "mode": "commercial",
        "label": "Lüks Islak Mermer & Yansıma",
        "subtitle": "Siyah mermer, su pırıltısı ve altın ışık kırılmaları",
        "description": "Kozmetik, parfüm veya mücevher için: Islak cilalı Nero Marquina siyah mermeri, ayna gibi yansıma ve sıcak amber ışık kırılmaları.",
        "badge": "Lüks",
        "samplePrompt": "Siyah ıslak mermer zemin üzerinde duran parfüm şişesi, altın ışık kırılmaları",
        "realPrompt": "Luxury commercial product photography, frosted glass cosmetic bottle on wet black Nero Marquina marble with crisp liquid reflection, gold metallic accents, subtle moody warm amber side-lighting, high-end perfumery aesthetic.",
        "imageUrl": "/styles/catalog/luxury_wet_marble.webp",
        "promptModifier": "Luxury commercial product photography, reflective wet black polished marble surface, crystal clear water puddle reflections, elegant warm side rim lighting, high-end perfumery ad",
        "tags": [
          "Lüks",
          "Mermer",
          "Kozmetik"
        ]
      },
      {
        "id": "natural_botanical_water",
        "mode": "commercial",
        "label": "Doğal Botanik & Su Damlası",
        "subtitle": "Yeşil yapraklar, kristal su dalgası ve sabah güneşi",
        "description": "Organik ve doğal ürünler için: Nemli yeşil monstera/okaliptüs yaprakları, berrak su dalgalanması ve canlandırıcı sabah gün ışığı.",
        "badge": "Organik",
        "samplePrompt": "Doğal yeşil yapraklar ve su damlaları arasında duran organik cilt bakım serumu",
        "realPrompt": "Clean organic beauty commercial photography, skincare serum bottle resting among lush dewy monstera leaves with crystal clear water ripples, morning sunbeams creating caustics, fresh rejuvenating atmosphere.",
        "imageUrl": "/styles/catalog/natural_botanical_water.webp",
        "promptModifier": "Organic product beauty commercial, lush green botanical leaves, crisp morning water droplets and gentle ripples, natural morning sunbeams with water caustics, fresh vibrant atmosphere",
        "tags": [
          "Botanik",
          "Organik",
          "Su"
        ]
      },
      {
        "id": "zero_gravity_float",
        "mode": "commercial",
        "label": "Sıfır Yerçekimi Süzülme (Float)",
        "subtitle": "Havada süzülen parçalar, patlatılmış detaylar ve dinamik açı",
        "description": "Spor ayakkabı, kulaklık veya teknolojik aksesuarlar için: Yerçekimsiz alanda parçaları havada süzülen ürün ve yüksek hızlı stüdyo flaşı.",
        "badge": "Dinamik",
        "samplePrompt": "Parçaları havada süzülen spor ayakkabı, sıfır yerçekimi dinamik stüdyo çekimi",
        "realPrompt": "Dynamic zero-gravity floating product photography, modern athletic sneaker suspended mid-air with disassembled sole layers and glowing air bubble, floating water droplets, high-speed shutter capture, commercial sports advertising.",
        "imageUrl": "/styles/catalog/zero_gravity_float.webp",
        "promptModifier": "Zero gravity floating product shot, product parts suspended mid-air in dynamic explosion composition, high-speed flash capture, modern clean commercial background",
        "tags": [
          "Float",
          "Yerçekimsiz",
          "Dinamik"
        ]
      }
    ]
  },
  {
    "id": "outpaint_relight",
    "label": "Arka Plan, Sahne Genişletme & Işık Eşleme",
    "shortLabel": "Genişletme & Işık",
    "description": "Arka planı saydamlaştır (PNG cutout), sahneyi 16:9 genişlet veya ışığı yeniden eşle.",
    "iconName": "Maximize2",
    "coverUrl": "/styles/catalog/outpaint_to_widescreen.webp",
    "accentColor": "#14b8a6",
    "requiresUpload": true,
    "acceptedUploadKinds": [
      "image"
    ],
    "uploadHint": "Genişletilecek, dekupe edilecek veya ışığı değiştirilecek fotoğrafı yükle",
    "subcategories": [
      {
        "id": "transparent_cutout",
        "mode": "outpaint_relight",
        "label": "Saydam Arka Plan (PNG Cutout)",
        "subtitle": "Kusursuz saç ve kenar dekupe, transparan alfa kanalı",
        "description": "Kişi veya nesneyi arka plandan piksel hassasiyetinde ayır: Saç telleri ve saydam detaylar korunarak e-ticaret ve tasarım için temiz PNG cutout.",
        "badge": "PNG Cutout",
        "samplePrompt": "Karakteri arka plandan kusursuz saç telleriyle dekupe et, saydam arka plan",
        "realPrompt": "Studio product isolation photography, handcrafted luxury ceramic vessel centered on perfectly neutral background, crisp razor-sharp perimeter contours, natural contact ground shadow, ready for alpha cutout compositing.",
        "imageUrl": "/styles/catalog/transparent_cutout.webp",
        "promptModifier": "Isolated subject cutout photography, perfect alpha transparency contour, razor-sharp edge masking, fine hair strand preservation, clean studio lighting",
        "tags": [
          "Dekupe",
          "PNG",
          "Transparan"
        ]
      },
      {
        "id": "outpaint_to_widescreen",
        "mode": "outpaint_relight",
        "label": "Sahne Genişletme (Outpainting 16:9)",
        "subtitle": "Kare veya dikey görseli geniş sinematik sahneye tamamla",
        "description": "Mevcut görselin solunu, sağını veya üstünü çevre dokuyla tutarlı şekilde üret: Orijinal kompozisyonu bozmadan yatay YouTube banner veya sinema formatına çevir.",
        "badge": "Outpaint",
        "samplePrompt": "Görseli sağa ve sola doğru doğal çevre detaylarıyla 16:9 sinematik formata genişlet",
        "realPrompt": "Ultra-wide 16:9 panoramic landscape expansion, modern minimalist glass villa nestled among towering pine forest and dramatic mountain peaks at dawn, expansive atmospheric clouds and mist, cinematic scale.",
        "imageUrl": "/styles/catalog/outpaint_to_widescreen.webp",
        "promptModifier": "Seamless panoramic outpainting 16:9 ratio, expanding original scenery on left and right borders with contextually coherent environment, cinematic depth and uninterrupted texture continuity",
        "tags": [
          "Outpaint",
          "16:9",
          "Genişlet"
        ]
      },
      {
        "id": "studio_backdrop_replace",
        "mode": "outpaint_relight",
        "label": "Stüdyo Fonu ile Değiştir",
        "subtitle": "Ev ortamını profesyonel renkli stüdyo fonuna taşı",
        "description": "Kişiyi veya ürünü koruyarak arkadaki dağınık odayı minimalist profesyonel fotoğraf stüdyosu kağıt fonuyla (seamless paper) değiştir.",
        "badge": "Stüdyo",
        "samplePrompt": "Arka planı sıcak terracotta rengi pürüzsüz stüdyo sonsuzluk fonuyla değiştir",
        "realPrompt": "Commercial fashion portrait on seamless studio paper backdrop in warm terracotta clay tone, professional Profoto octabox soft lighting, beautiful natural model styling, clean catalog commercial finish.",
        "imageUrl": "/styles/catalog/studio_backdrop_replace.webp",
        "promptModifier": "Studio backdrop replacement, seamless smooth photography paper backdrop in custom elegant tone, subtle curved infinity cyc wall shadow, professional key light balance",
        "tags": [
          "Stüdyo",
          "Fon",
          "Temizleme"
        ]
      },
      {
        "id": "relight_golden_hour",
        "mode": "outpaint_relight",
        "label": "Akıllı Işık Eşleme (Altın Saat)",
        "subtitle": "Yüzü ve sahneyi sıcak gün batımı güneşiyle yeniden aydınlat",
        "description": "Yapay flaşlı veya soluk fotoğrafları sinematik altın saat ışığına dönüştür: Saçlarda sıcak altın kontur ışığı ve yumuşak gölgeler.",
        "badge": "Relight",
        "samplePrompt": "Karakterin üzerine vuran ışığı sıcak akşamüstü altın saat güneşine dönüştür",
        "realPrompt": "Cinematic portrait relighting with low golden hour Mediterranean sun, warm directional side-light wrapping around face and hair highlights, natural lens flare, soft atmospheric dust motes, warm glow.",
        "imageUrl": "/styles/catalog/relight_golden_hour.webp",
        "promptModifier": "AI portrait relighting, low golden hour warm directional sunlight from side, hair rim lighting halo, warm color temperature harmonization, soft cinematic shadows",
        "tags": [
          "Relight",
          "Altın Saat",
          "Işık"
        ]
      }
    ]
  },
  {
    "id": "portrait_retouch",
    "label": "Portre Rötuş & Güzelleştirme",
    "shortLabel": "Portre Rötuş",
    "description": "Yapay zeka plastikliğine düşmeden doğal cilt dokusunu koruyan üst düzey rötuş.",
    "iconName": "UserCheck",
    "coverUrl": "/styles/catalog/natural_silk_skin.webp",
    "accentColor": "#f97316",
    "requiresUpload": true,
    "acceptedUploadKinds": [
      "image"
    ],
    "uploadHint": "Rötuş yapılacak portre veya selfie fotoğrafını yükle",
    "subcategories": [
      {
        "id": "natural_silk_skin",
        "mode": "portrait_retouch",
        "label": "Doğal Cilt Dokusu & Temizleme",
        "subtitle": "Pore-preserving doğal rötuş, anti-plastik gerçekçilik",
        "description": "Yapay zeka bulanıklığı ve plastik ten efekti olmadan: Cilt gözeneklerini ve mikro dokuyu koruyarak sivilce, parlama ve kızarıklıkları temizler.",
        "badge": "Anti-AI",
        "samplePrompt": "Gözenekleri koruyarak cilt tonunu eşitle, doğal ve taze editoryal portre",
        "realPrompt": "Ultra-high-definition beauty portrait, natural skin texture with preserved pores, gentle skin tone equalization, bright lively eyes with subtle catchlights, soft natural beauty dish lighting, anti-plastic authentic finish.",
        "imageUrl": "/styles/catalog/natural_silk_skin.webp",
        "promptModifier": "High-end frequency separation portrait retouching, authentic skin pore micro-texture preserved, anti-smooth non-plastic realistic complexion, natural eye sparkle, professional beauty retouch",
        "tags": [
          "Cilt",
          "Doğal",
          "Rötuş"
        ]
      },
      {
        "id": "rembrandt_studio_light",
        "mode": "portrait_retouch",
        "label": "Rembrandt Stüdyo Portresi",
        "subtitle": "Yanakta klasik ışık üçgeni ve derin ressam tonları",
        "description": "Tarihi Rembrandt ışık kurulumu: Gölgedeki yanak üzerinde beliren imza ışık üçgeni, koyu dokulu tuval fon ve derin göz ışıltısı.",
        "badge": "Klasik",
        "samplePrompt": "Rembrandt aydınlatması ile çekilmiş derin ve duygulu stüdyo portresi",
        "realPrompt": "Masterpiece portrait photography using classic Rembrandt lighting, distinctive luminous triangle of light on the shadowed cheek, dark textured painterly canvas background, rich skin depth and chiaroscuro tonal nuance.",
        "imageUrl": "/styles/catalog/rembrandt_studio_light.webp",
        "promptModifier": "Masterclass Rembrandt lighting portrait, classic triangle of light on the shadow cheek, rich chiaroscuro contrast, timeless painterly studio depth",
        "tags": [
          "Rembrandt",
          "Stüdyo",
          "Işık"
        ]
      },
      {
        "id": "linkedin_corporate_headshot",
        "mode": "portrait_retouch",
        "label": "LinkedIn & Kurumsal Headshot",
        "subtitle": "Temiz iş dünyası portresi, takım elbise ve kurumsal arka plan",
        "description": "Selfie veya amatör fotoğrafı anında Fortune 500 yönetici seviyesinde profesyonel headshot görseline dönüştür.",
        "badge": "Kariyer",
        "samplePrompt": "Modern cam ofis arka planında gülümseyen profesyonel kurumsal portre",
        "realPrompt": "Modern executive corporate headshot, confident friendly professional in tailored navy wool blazer, softly blurred bright modern architectural office atrium in background, clean flattering ring lighting.",
        "imageUrl": "/styles/catalog/linkedin_corporate_headshot.webp",
        "promptModifier": "Professional executive corporate headshot, modern blurred glass architectural office background, crisp business attire, confident approachable expression, clean key lighting",
        "tags": [
          "LinkedIn",
          "Kurumsal",
          "Headshot"
        ]
      }
    ]
  },
  {
    "id": "artistic_collage",
    "label": "Sanatsal Kolaj & Zine",
    "shortLabel": "Kolaj & Zine",
    "description": "Fotoğrafları yırtık kağıt, polaroid masası ve karma medya ile birleştir.",
    "iconName": "Layers",
    "coverUrl": "/styles/catalog/polaroid_scrapbook_table.webp",
    "accentColor": "#a855f7",
    "requiresUpload": false,
    "acceptedUploadKinds": [
      "image"
    ],
    "uploadHint": "İsteğe bağlı: Kolaja dahil edilecek 1 veya daha fazla görsel yükle",
    "subcategories": [
      {
        "id": "polaroid_scrapbook_table",
        "mode": "artistic_collage",
        "label": "Polaroid Anı Masası (Scrapbook)",
        "subtitle": "Masaya saçılmış polaroidler, ataçlar ve el yazısı notlar",
        "description": "Ahşap çalışma masası üzerine dağılmış nostaljik polaroid fotoğraflar, bantlar ve vintage anı defteri hissi.",
        "badge": "Nostalji",
        "samplePrompt": "Ahşap masa üzerinde dağınık duran yaz tatili polaroid fotoğrafları ve el yazısı notlar",
        "realPrompt": "Artistic scrapbook flat-lay table, authentic Polaroid instant photos with handwritten white border notes, washi tape, dried lavender sprigs, vintage European postage stamps, rustic wooden table surface, warm nostalgic memories.",
        "imageUrl": "/styles/catalog/polaroid_scrapbook_table.webp",
        "promptModifier": "Nostalgic polaroid scrapbook scatter on dark wooden desk, authentic white polaroid frames, washi tape corners, handwritten scribbles, warm overhead reading lamp light",
        "tags": [
          "Polaroid",
          "Scrapbook",
          "Anı"
        ]
      },
      {
        "id": "punk_risograph_fanzine",
        "mode": "artistic_collage",
        "label": "Punk Risograph Fanzine",
        "subtitle": "Yırtık gazete küpürleri, koli bandı ve iki renkli baskı",
        "description": "Yeraltı kültürünün ham görsel dili: yarı ton baskı tramları, asimetrik kırpıntılar ve cesur punk estetiği.",
        "badge": "Punk",
        "samplePrompt": "Yırtık dergi sayfaları ve neon mor-siyah baskı tramlı deneysel sanat kolajı",
        "realPrompt": "Underground punk fanzine cover, bold distressed cut-out typography, high-contrast photocopier halftone noise, torn paper collage layers, vibrant neon ink splatters, rebellious DIY garage rock aesthetic.",
        "imageUrl": "/styles/catalog/punk_risograph_fanzine.webp",
        "promptModifier": "Dadaist punk risograph zine collage, torn magazine clippings, clear packing tape textures, halftone photo prints, high contrast two-tone ink aesthetic",
        "tags": [
          "Zine",
          "Punk",
          "Dada"
        ]
      }
    ]
  }
]
